import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useCallback, useRef} from 'react'

import {omit} from '../../../../utils/omit'
import type {Repository} from '../../../api/common-contracts'
import {apiApplyTemplate} from '../../../api/memex/api-apply-template'
import type {ApplyTemplateRequest} from '../../../api/memex/contracts'
import {apiRepositoryBulkAddItems} from '../../../api/memex-items/api-repository-bulk-add-items'
import type {RepositoryBulkAddMemexItemsRequest} from '../../../api/memex-items/contracts'
import {DefaultOmitPropertiesForView} from '../../../api/view/contracts'
import {apiCreateWorkflow} from '../../../api/workflows/api-create-workflow'
import {MemexWorkflowContentType} from '../../../api/workflows/contracts'
import type {SelectedTemplate} from '../../../components/template-dialog/hooks/use-template-link'
import useToasts from '../../../components/toasts/use-toasts'
import {getViewTypeParamFromViewType} from '../../../helpers/view-type'
import {isAutoAddWorkflow} from '../../../helpers/workflow-utilities'
import {useApiRequest} from '../../../hooks/use-api-request'
import {useUpdateViewWithoutSettingStates} from '../../../hooks/use-view-apis'
import {useViews} from '../../../hooks/use-views'
import {useBulkAddNotifications} from '../../../state-providers/bulk-add-notifications/bulk-add-notifications-state-provider'
import {useSingleDataRefresh} from '../../../state-providers/data-refresh/use-single-data-refresh'
import {useUpdateMemexSettings} from '../../../state-providers/memex/use-update-memex-settings'
import {useWorkflows} from '../../../state-providers/workflows/use-workflows'
import {BulkCopyResources} from '../../../strings'

const INITIAL_BULK_IMPORT_COUNT = 500

export type ApplyTemplateArgs = {
  title: string
  template: SelectedTemplate
  rollback?: () => void
  autoAddEnabledRepository?: Repository
  autoAddEntities?: Array<'issues' | 'prs'>
  query?: string
  expectedItemCount?: number
  onServerError?: () => void
  onSuccess?: () => void
}

export function useApplyTemplate() {
  const handleDataRefresh = useSingleDataRefresh()
  const {addToast} = useToasts()
  const copyingDraftsAsync = useRef(false)
  const memex_create_project_from_query = useFeatureFlag('memex_create_project_from_query')

  const {viewsMap} = useViews()
  const updateView = useUpdateViewWithoutSettingStates()
  const workflows = useWorkflows()
  const {notifyBulkAddStarted, notifyNoItemsToImport} = useBulkAddNotifications()

  const {updateSettings} = useUpdateMemexSettings()
  const applyTemplateRequest = useApiRequest({
    request: async (requestData: ApplyTemplateRequest) => {
      const res = await apiApplyTemplate(requestData)
      if (res.copyingDraftsAsync) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({message: BulkCopyResources.copyingDraftsMessage, type: 'default', keepAlive: false})
        copyingDraftsAsync.current = true
      }
    },
    rollback: (rollbackAction?: () => void) => {
      rollbackAction?.()
    },
    showErrorToast: true,
  })

  const applyTemplate = useCallback(
    async function ({
      template,
      title,
      rollback,
      autoAddEnabledRepository,
      autoAddEntities,
      query,
      expectedItemCount,
      onServerError,
      onSuccess,
    }: ApplyTemplateArgs) {
      const actions: Array<Promise<unknown>> = [
        updateSettings({title, defaultIssueCreateRepository: autoAddEnabledRepository}),
      ]
      const contentTypes: Array<MemexWorkflowContentType> = []
      const queryEntities: Array<string> = []
      // User intended to bulk add (regardless of whether there are items)
      const userIntendedBulkAdd =
        Boolean(memex_create_project_from_query && query) ||
        (autoAddEnabledRepository && autoAddEntities && autoAddEntities.length > 0)
      const shouldEnableAutoAddWorkflow = autoAddEnabledRepository && autoAddEntities && autoAddEntities.length > 0

      for (const entity of autoAddEntities ?? []) {
        if (entity === 'issues') {
          contentTypes.push(MemexWorkflowContentType.Issue)
          queryEntities.push('issue')
        } else if (entity === 'prs') {
          contentTypes.push(MemexWorkflowContentType.PullRequest)
          queryEntities.push('pr')
        }
      }
      if (template.type === 'layout') {
        const view = viewsMap[1]
        if (view) {
          actions.push(
            updateView.perform({
              viewNumber: view.number,
              view: omit(
                {
                  ...view.serverViewState,
                  layout: getViewTypeParamFromViewType(template.viewType),
                },
                DefaultOmitPropertiesForView,
              ),
            }),
          )
        }

        // If we have repository-based auto-add settings, create or update the auto-add workflow
        if (shouldEnableAutoAddWorkflow) {
          const workflow = workflows.workflows.find(isAutoAddWorkflow)
          const getAction = workflow?.actions[0]
          const addAction = workflow?.actions[1]
          if (workflow && getAction && addAction) {
            const updatedGetAction = {
              ...getAction,
              arguments: {
                repositoryId: autoAddEnabledRepository?.id,
                query: `is:${queryEntities.join(',')} is:open`,
              },
            }
            const updatedAddAction = {
              ...addAction,
              arguments: {
                repositoryId: autoAddEnabledRepository?.id,
              },
            }
            actions.push(
              apiCreateWorkflow({
                workflow: {
                  name: workflow.name,
                  enabled: true,
                  contentTypes,
                  triggerType: workflow.triggerType,
                  actions: [updatedGetAction, updatedAddAction],
                },
              }),
            )
          }
        }
      } else {
        const templateToApply =
          template.type === 'custom' ? String(template.template.projectNumber) : template.template.id
        const autoEnableArgs =
          template.type === 'custom'
            ? {}
            : {autoAddEnabledRepositoryId: autoAddEnabledRepository?.id, autoAddEntities, query}
        actions.push(
          applyTemplateRequest.perform(
            {
              template: templateToApply,
              ...autoEnableArgs,
            },
            rollback,
          ),
        )
      }

      if (userIntendedBulkAdd) {
        if (expectedItemCount === 0) {
          // Skip bulk add only if we know for certain there are zero items (expectedItemCount === 0).
          actions.push(Promise.resolve().then(() => notifyNoItemsToImport()))
        } else {
          let bulkAddRequest: RepositoryBulkAddMemexItemsRequest | undefined
          if (memex_create_project_from_query && query) {
            bulkAddRequest = {
              count: INITIAL_BULK_IMPORT_COUNT,
              q: query,
              initialImport: true,
            }
          } else if (autoAddEnabledRepository && autoAddEntities?.length && queryEntities.length) {
            bulkAddRequest = {
              repositoryId: autoAddEnabledRepository.id,
              count: INITIAL_BULK_IMPORT_COUNT,
              contentTypes: queryEntities,
              initialImport: true,
            }
          }

          if (bulkAddRequest) {
            actions.push(
              apiRepositoryBulkAddItems(bulkAddRequest).then(([success, requestId]) => {
                if (success && requestId) {
                  notifyBulkAddStarted(requestId, !!query)
                }
              }),
            )
          }
        }
      }
      const results = await Promise.allSettled(actions)

      if (results.some(result => result.status === 'rejected')) {
        onServerError?.()
        return
      }

      // Refresh the project with all new data
      await handleDataRefresh()

      // Call success only if no errors occurred
      onSuccess?.()
    },
    [
      applyTemplateRequest,
      handleDataRefresh,
      updateSettings,
      updateView,
      viewsMap,
      workflows,
      notifyBulkAddStarted,
      notifyNoItemsToImport,
      memex_create_project_from_query,
    ],
  )

  const isApplyingTemplate = applyTemplateRequest.status.current.status === 'loading'

  return {applyTemplate, isApplyingTemplate, copyingDraftsAsync}
}
